import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/EquipmentReturn.css';
import API_BASE from '../config';

const API = `${API_BASE}/admin`;
const CONDITIONS = [
  { value: 'good', label: '✔ Good / Добро' },
  { value: 'damaged', label: '⚠ Damaged / Повредено' },
  { value: 'missing', label: '✖ Missing / Липсващо' },
  { value: 'not_returned', label: '⊘ Not Returned / Не върнато' },
];
const ITEM_KEYS = ['computer', 'monitor1', 'monitor2', 'token', 'other'];
const ITEM_LABELS = {
  computer: 'PC / Компютър',
  monitor1: 'Monitor 1 / Монитор 1',
  monitor2: 'Monitor 2 / Монитор 2',
  token: 'MFA Token / OneSpan',
  other: 'Other / Друго',
};

function fmtDate(iso) {
  return new Date(iso).toLocaleDateString('en-GB');
}

function getPendingRowStyle(entry) {
  if (entry.offboarding) return { border: '2px solid #ef4444', background: '#fff5f5' };
  const hasChange = entry.assignments.some(a => a.equipmentType === 'change');
  if (hasChange) return { border: '2px solid #7c3aed', background: '#faf5ff' };
  return { border: '2px solid #d97706', background: '#fffbeb' };
}

function getPendingBadge(entry) {
  if (entry.offboarding) return { label: 'LEAVING', color: '#dc2626', bg: '#fee2e2' };
  const hasChange = entry.assignments.some(a => a.equipmentType === 'change');
  if (hasChange) return { label: 'REPLACEMENT', color: '#7c3aed', bg: '#ede9fe' };
  return { label: 'ACTIVE', color: '#d97706', bg: '#fef3c7' };
}

export default function EquipmentReturn() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const [activeTab, setActiveTab] = useState('dashboard');

  // Dashboard state
  const [pending, setPending] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [pendingError, setPendingError] = useState('');

  // Process return state
  const [searchId, setSearchId] = useState('');
  const [looking, setLooking] = useState(false);
  const [assignments, setAssignments] = useState(null);
  const [lookupError, setLookupError] = useState('');
  const [returnState, setReturnState] = useState({});
  const [globalNotes, setGlobalNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [damageFiles, setDamageFiles] = useState({});           // assignmentId → FileList
  const [photoStatus, setPhotoStatus] = useState({});           // assignmentId → 'uploading'|'done'|'failed'
  const [photoErrors, setPhotoErrors] = useState({});            // assignmentId → error message string
  const [confirmedPhotoUrls, setConfirmedPhotoUrls] = useState({}); // assignmentId → string[] (uploaded Supabase paths)

  const fetchPending = useCallback(async () => {
    try {
      const res = await fetch(`${API}/equipment-pending-returns`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        setPending(data.data || []);
      }
    } catch (e) {
      setPendingError('Error connecting to server.');
    } finally {
      setPendingLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const handleLookup = async (e, overrideId) => {
    if (e) e.preventDefault();
    const empId = overrideId || searchId.trim();
    if (!empId) return;
    setLooking(true);
    setLookupError('');
    setAssignments(null);
    setReturnState({});
    setSuccess('');
    setError('');
    setDamageFiles({});
    setPhotoStatus({});
    setPhotoErrors({});
    try {
      const res = await fetch(`${API}/equipment-assignments/employee/${empId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) { setLookupError(data.message || 'Failed to look up employee'); return; }
      if (!data.assignments || data.assignments.length === 0) {
        setLookupError('No active equipment assignments found for this Employee ID.');
        return;
      }
      setAssignments(data.assignments);
      const init = {};
      data.assignments.forEach(a => {
        const state = { condition: 'good', itemNotes: '' };
        ITEM_KEYS.forEach(k => {
          const hasSerial = !!(a[k] && a[k] !== 'NA');
          const alreadyReturned = !!a[k + 'Returned'];
          state[k + 'Returned'] = hasSerial && !alreadyReturned;
        });
        init[a.id] = state;
      });
      setReturnState(init);
    } catch (e) {
      setLookupError('Error connecting to server.');
    } finally {
      setLooking(false);
    }
  };

  const handleDashboardSelect = (empId) => {
    setSearchId(empId);
    setActiveTab('process');
    setConfirmedPhotoUrls({});
    // Auto-trigger lookup so the user doesn't have to click "Look Up" again
    handleLookup(null, empId);
  };

  const updateItem = (assignId, field, value) => {
    setReturnState(prev => ({
      ...prev,
      [assignId]: { ...prev[assignId], [field]: value }
    }));
  };

  const anySelected = assignments && assignments.some(a => {
    const s = returnState[a.id] || {};
    // not_returned counts as a selection (documented in PDF even if not physically returned)
    if (s.condition === 'not_returned') return true;
    return ITEM_KEYS.some(k => s[k + 'Returned']);
  });

  const handlePreUploadPhotos = async (assignmentId) => {
    const files = damageFiles[assignmentId];
    if (!files || files.length === 0) return;
    setPhotoStatus(prev => ({ ...prev, [assignmentId]: 'uploading' }));
    try {
      const fd = new FormData();
      Array.from(files).forEach(f => fd.append('photos', f));
      const res = await fetch(`${API}/equipment-assignments/${assignmentId}/damage-photos`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd
      });
      const data = await res.json();
      if (res.ok) {
        setConfirmedPhotoUrls(prev => ({ ...prev, [assignmentId]: data.paths || [] }));
        setPhotoStatus(prev => ({ ...prev, [assignmentId]: 'done' }));
      } else {
        const msg = data.message || 'Upload failed';
        console.error('[Photo upload error]', msg);
        setPhotoStatus(prev => ({ ...prev, [assignmentId]: 'failed' }));
        setPhotoErrors(prev => ({ ...prev, [assignmentId]: msg }));
      }
    } catch (err) {
      setPhotoStatus(prev => ({ ...prev, [assignmentId]: 'failed' }));
      setPhotoErrors(prev => ({ ...prev, [assignmentId]: err.message || 'Network error' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!anySelected) { setError('Select at least one item to return.'); return; }
    setSubmitting(true);
    setError('');
    setSuccess('');

    // Auto-upload any damage photos not yet uploaded (user may skip the Upload button)
    let finalConfirmedUrls = { ...confirmedPhotoUrls };
    const needsUpload = assignments.filter(a => {
      const s = returnState[a.id] || {};
      return s.condition === 'damaged' && damageFiles[a.id]?.length > 0 && !finalConfirmedUrls[a.id];
    });
    for (const a of needsUpload) {
      setPhotoStatus(prev => ({ ...prev, [a.id]: 'uploading' }));
      try {
        const fd = new FormData();
        Array.from(damageFiles[a.id]).forEach(f => fd.append('photos', f));
        const r = await fetch(`${API}/equipment-assignments/${a.id}/damage-photos`, {
          method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd
        });
        const d = await r.json();
        if (r.ok) {
          finalConfirmedUrls[a.id] = d.paths || [];
          setConfirmedPhotoUrls(prev => ({ ...prev, [a.id]: d.paths || [] }));
          setPhotoStatus(prev => ({ ...prev, [a.id]: 'done' }));
        } else {
          setPhotoStatus(prev => ({ ...prev, [a.id]: 'failed' }));
          setPhotoErrors(prev => ({ ...prev, [a.id]: d.message || 'Upload failed' }));
        }
      } catch (err) {
        setPhotoStatus(prev => ({ ...prev, [a.id]: 'failed' }));
        setPhotoErrors(prev => ({ ...prev, [a.id]: err.message }));
      }
    }

    const assignmentReturns = assignments
      .filter(a => {
        const s = returnState[a.id] || {};
        if (s.condition === 'not_returned') return true;
        return ITEM_KEYS.some(k => s[k + 'Returned']);
      })
      .map(a => ({
        id: a.id,
        ...returnState[a.id],
        damagePhotoUrls: finalConfirmedUrls[a.id] || []
      }));

    const emp = assignments[0];
    try {
      const res = await fetch(`${API}/equipment-return`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employeeId: emp.employeeId,
          employeeName: emp.employeeName,
          departmentName: emp.departmentName || '',
          assignmentReturns,
          notes: globalNotes.trim() || null
        })
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message || 'Failed to process return.'); return; }

      const photoCount = Object.values(finalConfirmedUrls).flat().length;
      const photoNote = photoCount > 0 ? ` ${photoCount} damage photo(s) embedded in protocol.` : '';

      const emailNote = data.returnEmailResult?.sent
        ? ' Return protocol emailed to employee.'
        : data.returnEmailResult?.reason ? ` (Email: ${data.returnEmailResult.reason})` : '';
      setSuccess('Equipment return processed. Return protocol PDF generated.' + photoNote + emailNote);
      setAssignments(null);
      setReturnState({});
      setSearchId('');
      setGlobalNotes('');
      setDamageFiles({});
      setPhotoStatus({});
      setPhotoErrors({});
      setConfirmedPhotoUrls({});
      fetchPending();
    } catch (e) {
      setError('Error connecting to server.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="eq-return-page">
      <div className="eq-return-header">
        <button className="btn-back" onClick={() => navigate('/admin')}>← Back</button>
        <h1>Equipment Return</h1>
        <p className="subtitle">Process asset return for departing or transferring employees</p>
      </div>

      {/* Tabs */}
      <div className="return-tabs">
        <button
          className={`return-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('dashboard')}
        >
          📋 Pending Returns
          {pending.length > 0 && <span className="tab-badge">{pending.length}</span>}
        </button>
        <button
          className={`return-tab ${activeTab === 'process' ? 'active' : ''}`}
          onClick={() => setActiveTab('process')}
        >
          ↩ Process Return
        </button>
      </div>

      {/* ── DASHBOARD ──────────────────────────────────── */}
      {activeTab === 'dashboard' && (
        <div className="dashboard-panel">
          <div className="dashboard-legend">
            <span className="dash-legend-chip chip-red">Leaving Employee</span>
            <span className="dash-legend-chip chip-purple">Replacement / Change</span>
            <span className="dash-legend-chip chip-amber">Active (no offboarding)</span>
          </div>

          {pendingLoading && <div className="eq-loading">Loading…</div>}
          {pendingError && <div className="lookup-error">⚠ {pendingError}</div>}

          {!pendingLoading && pending.length === 0 && (
            <div className="dash-empty">✔ No pending returns at this time.</div>
          )}

          {!pendingLoading && pending.map(entry => {
            const badge = getPendingBadge(entry);
            const rowStyle = getPendingRowStyle(entry);
            const items = entry.assignments.flatMap(a =>
              ITEM_KEYS
                .filter(k => a[k] && a[k] !== 'NA' && !a[k + 'Returned'])
                .map(k => ({ label: ITEM_LABELS[k], value: a[k], type: a.equipmentType }))
            );

            return (
              <div key={entry.employeeId} className="dash-row" style={rowStyle}>
                <div className="dash-row-top">
                  <div className="dash-emp">
                    <span className="dash-name">{entry.employeeName}</span>
                    <span className="dash-id">ID: {entry.employeeId}</span>
                    {entry.departmentName && <span className="dash-dept">{entry.departmentName}</span>}
                  </div>
                  <div className="dash-meta">
                    <span className="dash-badge" style={{ color: badge.color, background: badge.bg }}>
                      {badge.label}
                    </span>
                    {entry.offboarding && (
                      <span className="dash-offboard-date">
                        Leave date: {fmtDate(entry.offboarding.offboardDate)}
                      </span>
                    )}
                    <span className="dash-item-count">
                      {items.length} item{items.length !== 1 ? 's' : ''} to return
                    </span>
                  </div>
                  <button
                    className="dash-process-btn"
                    onClick={() => handleDashboardSelect(entry.employeeId)}
                  >
                    Process Return →
                  </button>
                </div>
                <div className="dash-items">
                  {items.map((item, i) => (
                    <span
                      key={i}
                      className={`dash-item-chip ${item.type === 'change' ? 'chip-purple-light' : ''}`}
                    >
                      {item.label}: <strong>{item.value}</strong>
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── PROCESS RETURN ─────────────────────────────── */}
      {activeTab === 'process' && (
        <>
          <div className="eq-return-card">
            <h2 className="card-title">Step 1 — Look up Employee by ID</h2>
            <form className="lookup-form" onSubmit={handleLookup}>
              <input
                type="text"
                placeholder="Enter Employee ID (Success Factor ID)…"
                value={searchId}
                onChange={e => setSearchId(e.target.value.replace(/\D/g, ''))}
                required
              />
              <button type="submit" disabled={looking}>
                {looking ? 'Searching…' : 'Look Up'}
              </button>
            </form>
            {lookupError && <div className="lookup-error">⚠ {lookupError}</div>}
            {success && <div className="return-success">✔ {success}</div>}
          </div>

          {assignments && (
            <form onSubmit={handleSubmit}>
              <div className="eq-return-card employee-info-card">
                <div className="emp-info-row">
                  <span><strong>Employee:</strong> {assignments[0].employeeName}</span>
                  <span><strong>ID:</strong> {assignments[0].employeeId}</span>
                  <span><strong>Dept:</strong> {assignments[0].departmentName || '—'}</span>
                  <span className="badge-count">
                    {assignments.length} active assignment{assignments.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>

              <h2 className="section-title">Step 2 — Select Items Being Returned</h2>
              <p className="section-hint">
                All active equipment is pre-selected. Uncheck anything NOT being returned now.
              </p>

              {assignments.map(a => {
                const s = returnState[a.id] || {};
                const hasItems = ITEM_KEYS.some(k => a[k] && a[k] !== 'NA');
                if (!hasItems) return null;
                return (
                  <div className="eq-return-card assignment-card" key={a.id}>
                    <div className="assignment-header">
                      <span className="assign-date">Assigned: {fmtDate(a.createdAt)}</span>
                      {a.createdBy && (
                        <span className="assign-by">
                          by {a.createdBy.firstName} {a.createdBy.lastName}
                        </span>
                      )}
                      <span className="assign-type">
                        {a.equipmentType === 'new' ? 'New' : 'Change'}
                      </span>
                      <span className={`method-chip ${a.receiveMethod === 'self_pickup' ? 'chip-green' : 'chip-yellow'}`}>
                        {a.receiveMethod === 'self_pickup' ? 'Self-pickup' : 'Delivery'}
                      </span>
                    </div>

                    <div className="condition-row">
                      <label className="cond-label">Overall Condition:</label>
                      {CONDITIONS.map(c => (
                        <label
                          key={c.value}
                          className={`cond-option cond-${c.value} ${s.condition === c.value ? 'selected' : ''}`}
                        >
                          <input
                            type="radio"
                            name={`condition-${a.id}`}
                            value={c.value}
                            checked={s.condition === c.value}
                            onChange={() => updateItem(a.id, 'condition', c.value)}
                          />
                          {c.label}
                        </label>
                      ))}
                    </div>

                    {/* Damage photo upload — visible only when condition is damaged */}
                    {s.condition === 'damaged' && (
                      <div className="damage-photo-row">
                        <label className="damage-photo-label">📷 Damage Photos:</label>
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/gif,image/heic,image/heif,.jpg,.jpeg,.png,.gif,.heic,.heif"
                          multiple
                          className="damage-photo-input"
                          onChange={e => {
                            setDamageFiles(prev => ({ ...prev, [a.id]: e.target.files }));
                            setPhotoStatus(prev => ({ ...prev, [a.id]: null }));
                            setPhotoErrors(prev => { const n = {...prev}; delete n[a.id]; return n; });
                            setConfirmedPhotoUrls(prev => { const n = {...prev}; delete n[a.id]; return n; });
                          }}
                        />
                        {damageFiles[a.id]?.length > 0 && photoStatus[a.id] !== 'done' && (
                          <button
                            type="button"
                            className="btn-upload-photos"
                            disabled={photoStatus[a.id] === 'uploading'}
                            onClick={() => handlePreUploadPhotos(a.id)}
                          >
                            {photoStatus[a.id] === 'uploading' ? '⏳ Uploading…' : `⬆ Upload ${damageFiles[a.id].length} photo(s)`}
                          </button>
                        )}
                        {photoStatus[a.id] === 'done' && (
                          <span className="damage-photo-count">
                            ✔ {confirmedPhotoUrls[a.id]?.length || damageFiles[a.id]?.length} photo(s) ready — will be embedded in protocol
                          </span>
                        )}
                        {photoStatus[a.id] === 'failed' && (
                          <span className="damage-photo-count" style={{color:'#b00000'}}>⚠ {photoErrors[a.id] || 'Upload failed'} — try again</span>
                        )}
                      </div>
                    )}

                    {/* Not returned reason */}
                    {s.condition === 'not_returned' && (
                      <div className="not-returned-row">
                        <label className="not-returned-label">⊘ Reason not returned / Причина:</label>
                        <textarea
                          className="not-returned-reason"
                          placeholder="e.g. Employee kept laptop for remote work — to be returned on 15/03/2026"
                          value={s.notReturnedReason || ''}
                          onChange={e => updateItem(a.id, 'notReturnedReason', e.target.value)}
                          rows={2}
                        />
                      </div>
                    )}

                    <div className="items-grid">
                      {ITEM_KEYS.map(k => {
                        const serial = a[k];
                        if (!serial || serial === 'NA') return null;
                        const alreadyReturned = !!a[k + 'Returned'];
                        const checked = !!s[k + 'Returned'];
                        if (alreadyReturned) {
                          return (
                            <span key={k} className="item-chip item-already-returned">
                              <span className="item-name">{ITEM_LABELS[k]}</span>
                              <span className="item-serial">{serial}</span>
                              <span className="already-badge">✔ Already returned</span>
                            </span>
                          );
                        }
                        return (
                          <label
                            key={k}
                            className={`item-chip ${checked ? 'item-checked' : 'item-unchecked'}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={e => updateItem(a.id, k + 'Returned', e.target.checked)}
                            />
                            <span className="item-name">{ITEM_LABELS[k]}</span>
                            <span className="item-serial">{serial}</span>
                          </label>
                        );
                      })}
                    </div>

                    <div className="notes-row">
                      <label>Notes for this assignment (optional):</label>
                      <input
                        type="text"
                        placeholder="e.g. Screen cracked, power cable missing…"
                        value={s.itemNotes || ''}
                        onChange={e => updateItem(a.id, 'itemNotes', e.target.value)}
                      />
                    </div>
                  </div>
                );
              })}

              <div className="eq-return-card">
                <label className="global-notes-label">
                  General Notes / Общи забележки (optional):
                </label>
                <textarea
                  rows={3}
                  placeholder="Additional notes about the return…"
                  value={globalNotes}
                  onChange={e => setGlobalNotes(e.target.value)}
                />
              </div>

              {error && <div className="return-error">⚠ {error}</div>}

              <div className="submit-row">
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={submitting || !anySelected}
                >
                  {submitting ? 'Processing…' : '✔ Process Return & Generate Protocol'}
                </button>
              </div>
            </form>
          )}
        </>
      )}
    </div>
  );
}

